import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Book, Lock, CheckCircle, Clock } from 'lucide-react';
import { getTrainingChannels, getTrainingProgress } from '../utils/api';

const TrainingVault = () => {
  const [channels, setChannels] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [channelsData, progressData] = await Promise.all([
        getTrainingChannels(),
        getTrainingProgress()
      ]);
      
      setChannels(channelsData);
      setProgress(progressData.progress_by_channel || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching training data:', error);
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      'search': '🔍',
      'target': '🎯',
      'shield-check': '✅',
      'map-pin': '📍',
      'facebook': '📘',
      'globe': '🌐'
    };
    return icons[iconName] || '📚';
  };

  if (loading) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="text-light-gray">Loading training vault...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout variant="light">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            🎓 Training Vault
          </h1>
          <p className="text-lg text-light-gray">
            Learn to market your own business — simple step-by-step courses on Google Ads, Facebook, and getting found on Google
          </p>
        </div>

        {/* Quick Link to Playbooks */}
        <Link
          to="/playbooks"
          className="card hover:scale-102 transition-all duration-200 mb-8 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <div className="text-4xl">📚</div>
            <div>
              <h3 className="text-xl font-bold text-white">Marketing Playbooks</h3>
              <p className="text-sm text-light-gray">Step-by-step guides for Google Ads, Facebook & getting found on Google</p>
            </div>
          </div>
          <span className="text-construction-orange font-medium">Open Playbooks →</span>
        </Link>

        {/* Channels Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {channels.map((channel) => {
            const channelProgress = progress[channel.id] || { completed: 0, total: channel.lesson_count, percentage: 0 };
            
            return (
              <Link
                key={channel.id}
                to={`/training/channel/${channel.id}`}
                className="card hover:scale-105 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{getIcon(channel.icon)}</div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{channel.name}</h3>
                      <p className="text-sm text-muted">{channel.lesson_count} lessons</p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-light-gray mb-4">
                  {channel.description}
                </p>

                {/* Progress Bar */}
                {channelProgress.completed > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-muted mb-1">
                      <span>{channelProgress.completed} of {channelProgress.total} complete</span>
                      <span>{Math.round(channelProgress.percentage)}%</span>
                    </div>
                    <div className="w-full bg-slate-blue rounded-full h-2">
                      <div
                        className="bg-construction-orange h-2 rounded-full transition-all duration-300"
                        style={{ width: `${channelProgress.percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-divider mt-4">
                  <span className="text-sm text-construction-orange font-medium">
                    Start Learning →
                  </span>
                  {channelProgress.completed === channelProgress.total && channelProgress.total > 0 && (
                    <CheckCircle className="text-green-400" size={20} />
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {channels.length === 0 && (
          <div className="text-center py-12">
            <Book className="mx-auto text-muted mb-4" size={48} />
            <p className="text-light-gray">Training content is being generated...</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrainingVault;
